// Instant-ticket barcode parsing.
//
// A ticket is printed as four groups, for example:
//
//     2740-0062765-053(023)
//     ^^^^ ^^^^^^^ ^^^  ^^^
//     game  pack    |    `-- NOT in the barcode, printed only
//                   `------- ticket number, the last 3 encoded digits
//
// Verified by decoding the scanned tickets (Interleaved 2 of 5): the barcode holds
// exactly 14 digits - game(4) + pack(7) + ticket(3). The value in brackets is never
// encoded, so a scanner cannot report it directly.
//
// The two three-digit groups are the same ticket counted from opposite ends of the
// pack: across every sampled ticket, `third + bracket === packSize + 1` (checked on
// packs of 50, 75 and 125). The bracketed value - the one the shop records as the
// ending number - is therefore derived from the scan plus the pack size.
export interface ParsedBarcode {
  gameNumber: string;
  packNumber: string;
  // the 3-digit group carried in the barcode; "" when the scan is too short
  ticketNumber: string;
}

// enough characters to read the game and pack
export const BARCODE_GAME_PACK_LENGTH = 11;
// a complete scan: game + pack + ticket
export const BARCODE_FULL_LENGTH = 14;

export const parseBarcode = (raw: string): ParsedBarcode => {
  const digits = (raw || "").replace(/\D/g, "");
  const ticket = digits.length >= BARCODE_FULL_LENGTH ? digits.substring(11, 14) : "";
  return {
    gameNumber: digits.substring(0, 4),
    packNumber: digits.substring(4, 11),
    ticketNumber: /^\d{3}$/.test(ticket) ? ticket : "",
  };
};

// Ending number for a scanned ticket: the bracketed value printed beside it.
// Returns "" when either input is missing or the result falls outside the pack,
// so a wrong pack size can never silently produce a plausible-looking number.
export const endNumberFromTicket = (ticketNumber: string, packSize: number): string => {
  const ticket = parseInt(ticketNumber, 10);
  const size = Number(packSize);
  if (!ticket || !size || Number.isNaN(ticket) || Number.isNaN(size)) return "";
  const end = size + 1 - ticket;
  if (end < 0 || end > size) return "";
  return String(end);
};
